import { useSyncExternalStore } from 'react';

export interface IStore<T> {
  /** Returns the current state snapshot. */
  getState: () => T;
  /** Merges a partial state patch (or the result of an updater function) into the current state and notifies all subscribers. */
  setState: (updater: Partial<T> | ((state: T) => Partial<T>)) => void;
  /** Subscribes a listener to state changes. Returns an unsubscribe function. */
  subscribe: (listener: () => void) => () => void;
  /** React hook that subscribes to a slice of state via `useSyncExternalStore`. */
  use: <U>(selector: (state: T) => U) => U;
}

/**
 * Creates a tiny external store that integrates with React via `useSyncExternalStore`.
 * @param initial - The initial state object.
 */
export const createStore = <T extends object>(initial: T): IStore<T> => {
  let state = initial;
  const listeners = new Set<() => void>();
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const getState = (): T => state;
  const setState = (updater: Partial<T> | ((state: T) => Partial<T>)): void => {
    const patch = typeof updater === 'function' ? updater(state) : updater;
    state = { ...state, ...patch };
    listeners.forEach(listener => listener());
  };
  const use = <U>(selector: (state: T) => U): U => {
    return useSyncExternalStore(
      subscribe,
      () => selector(state),
      () => selector(state)
    );
  };
  return { getState, setState, subscribe, use };
};
